import { useState, useMemo } from 'react';
import { createContext } from 'use-context-selector';
import type { FC } from 'react';
import { StepType } from 'src/types/step';
import useCustomer from 'src/hooks/useCustomer';
import useOrder from 'src/hooks/useOrder';

export interface OrderStepsContextValue {
  orderSteps: StepType[];
  currentOrderStep: number;
  setCurrentOrderStep: React.Dispatch<React.SetStateAction<number>>;
}

const OrderStepsContext = createContext<OrderStepsContextValue>({
  orderSteps: [],
  currentOrderStep: 0,
  setCurrentOrderStep: () => {},
});

export const OrderStepsProvider: FC<{ children }> = (props) => {
  const { children } = props;

  const customerNumber = useCustomer((state) => state.customer.number);
  const sfaOrderId = useOrder((state) => state?.order?.sfaOrderId);
  const isOpenOrderEdit = useOrder((state) => state?.order?.isOpenOrderEdit);

  const [currentOrderStep, setCurrentOrderStep] = useState<number>(0);

  // Memoize orderSteps so it only changes when dependencies actually change
  const orderSteps = useMemo(() => {
    if (!sfaOrderId) {
      return [];
    }

    return isOpenOrderEdit === true
      ? [
          { step: 1, title: 'Add Items', path: `/${customerNumber}/OrderEntry/${sfaOrderId}` },
          { step: 2, title: 'Edit Items', path: `/${customerNumber}/ItemEdit/${sfaOrderId}` },
          { step: 3, title: 'Order Header', path: `/${customerNumber}/OrderHeader/${sfaOrderId}` },
          { step: 4, title: 'Confirm Changes', path: `/${customerNumber}/OrderChangeConfirmation/${sfaOrderId}` },
        ]
      : [
          { step: 1, title: 'Add Items', path: `/${customerNumber}/OrderEntry/${sfaOrderId}` },
          { step: 2, title: 'Edit Items', path: `/${customerNumber}/ItemEdit/${sfaOrderId}` },
          { step: 3, title: 'Order Header', path: `/${customerNumber}/OrderHeader/${sfaOrderId}` },
          { step: 4, title: 'Finish & Submit', path: `/${customerNumber}/OrderSubmit/${sfaOrderId}` },
        ];
  }, [customerNumber, sfaOrderId, isOpenOrderEdit]);

  // Memoize the entire context value
  const contextValue = useMemo(() => ({
    orderSteps,
    currentOrderStep,
    setCurrentOrderStep,
  }), [orderSteps, currentOrderStep, setCurrentOrderStep]);

  return <OrderStepsContext.Provider value={contextValue}>{children}</OrderStepsContext.Provider>;
};

export default OrderStepsContext;
